import { MENTION_ALL_ATTR, MENTION_ATTR } from "@/lib/mentions";
import { parseFormatClass, type FormatKind } from "@/lib/richFormat";

/**
 * DOM operations of the rich composer on the contenteditable root: character formats that carry a
 * class (colour, highlight, size), inline code, code block, quote, link, horizontal rule and
 * "clear formatting". Bold / italic / underline / strike / lists go through document.execCommand
 * (the one reliable cross-browser path for those); everything here edits the DOM directly, and the
 * editor's own undo history (RichEditor) records the result.
 */

const ZWSP = "\u200B";
const INLINE_WRAPPERS = new Set(["STRONG", "B", "EM", "I", "U", "S", "STRIKE", "DEL", "CODE", "SPAN", "MARK", "FONT"]);
const STRUCTURE_PARENTS = new Set(["UL", "OL", "TABLE", "TBODY", "TR"]);

export function isChip(n: Node | null): boolean {
    return !!n && n.nodeType === 1 && ((n as Element).hasAttribute(MENTION_ATTR) || (n as Element).hasAttribute(MENTION_ALL_ATTR));
}

export function closestIn(node: Node | null, root: HTMLElement, match: (el: HTMLElement) => boolean): HTMLElement | null {
    let n: Node | null = node;
    while (n && n !== root) {
        if (n.nodeType === 1 && match(n as HTMLElement)) return n as HTMLElement;
        n = n.parentNode;
    }
    return null;
}

export function selectionRange(root: HTMLElement): Range | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const r = sel.getRangeAt(0);
    return root.contains(r.startContainer) && root.contains(r.endContainer) ? r : null;
}

export function setSelection(range: Range): void {
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
}

function hasContent(n: Node): boolean {
    if ((n.textContent ?? "") !== "") return true;
    return n.nodeType === 1 ? !!(n as Element).querySelector("br,img,hr,[" + MENTION_ATTR + "],[" + MENTION_ALL_ATTR + "]") : false;
}

/** Remove inline wrappers that hold nothing (what splitting and unwrapping leave behind). */
export function pruneEmptyInline(root: HTMLElement): void {
    const empties = Array.from(root.querySelectorAll("strong,b,em,i,u,s,strike,del,code,span,mark,a,font"))
        .filter((el) => !isChip(el) && !hasContent(el));
    empties.forEach((el) => el.remove());
}

/** Merge neighbours that are the same tag with the same class (so a recoloured sentence is one span, not ten). */
export function mergeAdjacent(root: HTMLElement): void {
    const all = Array.from(root.querySelectorAll("span,mark,code,a"));
    for (const el of all) {
        if (!el.isConnected || isChip(el)) continue;
        const next = el.nextSibling;
        if (next && next.nodeType === 1 && (next as Element).tagName === el.tagName
            && (next as Element).className === el.className
            && el.getAttribute("href") === (next as Element).getAttribute("href")
            && !isChip(next)) {
            while (next.firstChild) el.appendChild(next.firstChild);
            next.parentNode?.removeChild(next);
        }
    }
    root.normalize();
}

/** Text nodes touched by `range`, after splitting the text nodes at its ends. Selection-free. */
export function textNodesInRange(range: Range, root: HTMLElement): Text[] {
    if (range.collapsed) return [];
    // Split the end first so the start offset stays valid.
    const end = range.endContainer;
    if (end.nodeType === 3 && range.endOffset < (end as Text).length && range.endOffset > 0) {
        (end as Text).splitText(range.endOffset);
    }
    const start = range.startContainer;
    if (start.nodeType === 3 && range.startOffset > 0 && range.startOffset < (start as Text).length) {
        (start as Text).splitText(range.startOffset);
    }
    const out: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
        if (n.length === 0) continue;
        if (!range.intersectsNode(n)) continue;
        // intersectsNode is true for a node that only touches the boundary: skip those.
        if (n === range.endContainer && range.endOffset === 0) continue;
        if (n === range.startContainer && range.startOffset >= n.length) continue;
        const parent = n.parentElement;
        if (!parent || STRUCTURE_PARENTS.has(parent.tagName) || parent === root) {
            if (!/\S/.test(n.data)) continue;
        }
        if (closestIn(n, root, (el) => isChip(el) || el.tagName === "PRE")) continue;
        out.push(n);
    }
    return out;
}

function selectTexts(first: Text, last: Text): void {
    const r = document.createRange();
    r.setStart(first, 0);
    r.setEnd(last, last.length);
    setSelection(r);
}

/**
 * Give `t` a wrapper of its own inside `anc` by splitting `anc` around it: afterwards `anc` holds
 * only `t` (through whatever inline elements sat between), and its former neighbours are clones.
 */
function isolate(t: Node, anc: Element): void {
    const after = document.createRange();
    after.setStartAfter(t);
    after.setEnd(anc, anc.childNodes.length);
    if (!after.collapsed) {
        const frag = after.extractContents();
        if (hasContent(frag)) {
            const clone = anc.cloneNode(false);
            clone.appendChild(frag);
            anc.after(clone);
        }
    }
    const before = document.createRange();
    before.setStart(anc, 0);
    before.setEndBefore(t);
    if (!before.collapsed) {
        const frag = before.extractContents();
        if (hasContent(frag)) {
            const clone = anc.cloneNode(false);
            clone.appendChild(frag);
            anc.before(clone);
        }
    }
}

function unwrap(el: Element): void {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
}

const KIND_PREFIX: Record<FormatKind, string> = { c: "fg-c-", h: "fg-h-", s: "fg-s-", f: "fg-f-" };

function formatAncestor(node: Node, root: HTMLElement, kind: FormatKind): HTMLElement | null {
    return closestIn(node, root, (el) => Array.from(el.classList).some((c) => c.startsWith(KIND_PREFIX[kind]) && parseFormatClass(c)?.kind === kind));
}

function stripKindClasses(el: HTMLElement, kind: FormatKind): void {
    Array.from(el.classList).filter((c) => c.startsWith(KIND_PREFIX[kind])).forEach((c) => el.classList.remove(c));
}

function insertCaretWrapper(range: Range, el: HTMLElement): void {
    const text = document.createTextNode(ZWSP);
    el.appendChild(text);
    range.insertNode(el);
    const r = document.createRange();
    r.setStart(text, 1);
    r.collapse(true);
    setSelection(r);
}

/**
 * Apply (id) or remove (id = null) a text colour / highlight / size on the selection. With only a
 * caret, the new format applies to what is typed next.
 */
export function applyFormat(root: HTMLElement, kind: FormatKind, id: string | null): void {
    const range = selectionRange(root);
    if (!range) return;
    const tag = kind === "h" ? "mark" : "span";
    const cls = id ? `${KIND_PREFIX[kind]}${id}` : null;
    if (range.collapsed) {
        if (!cls) return;
        const el = document.createElement(tag);
        el.className = cls;
        insertCaretWrapper(range, el);
        return;
    }
    const texts = textNodesInRange(range, root);
    if (texts.length === 0) return;
    for (const t of texts) {
        const anc = formatAncestor(t, root, kind);
        if (anc) {
            isolate(t, anc);
            if (cls) {
                stripKindClasses(anc, kind);
                anc.classList.add(cls);
                // A <span> that held a colour and now a highlight (or the reverse) stays a span.
                if (kind === "h" && anc.tagName !== "MARK") {
                    const mark = document.createElement("mark");
                    mark.className = anc.className;
                    while (anc.firstChild) mark.appendChild(anc.firstChild);
                    anc.replaceWith(mark);
                }
            } else {
                stripKindClasses(anc, kind);
                if (anc.classList.length === 0) unwrap(anc);
            }
        } else if (cls) {
            const wrapper = document.createElement(tag);
            wrapper.className = cls;
            t.replaceWith(wrapper);
            wrapper.appendChild(t);
        }
    }
    pruneEmptyInline(root);
    mergeAdjacent(root);
    const live = texts.filter((t) => t.isConnected);
    if (live.length > 0) selectTexts(live[0]!, live[live.length - 1]!);
}

/** The class id of a kind at the caret/selection start, or null. */
export function formatAt(root: HTMLElement, kind: FormatKind): string | null {
    const sel = window.getSelection();
    const node = sel?.focusNode ?? null;
    if (!node || !root.contains(node)) return null;
    const anc = formatAncestor(node, root, kind);
    if (!anc) return null;
    for (const c of Array.from(anc.classList)) {
        const p = parseFormatClass(c);
        if (p && p.kind === kind) return p.id;
    }
    return null;
}

/** Wrap the selection in inline <code>, or unwrap it when the selection is already code. */
export function toggleInlineCode(root: HTMLElement): void {
    const range = selectionRange(root);
    if (!range) return;
    const inCode = (n: Node) => closestIn(n, root, (el) => el.tagName === "CODE");
    if (range.collapsed) {
        if (inCode(range.startContainer)) return;
        insertCaretWrapper(range, document.createElement("code"));
        return;
    }
    const texts = textNodesInRange(range, root);
    if (texts.length === 0) return;
    const allCode = texts.every((t) => inCode(t));
    for (const t of texts) {
        const anc = inCode(t);
        if (allCode && anc) {
            isolate(t, anc);
            unwrap(anc);
        } else if (!anc) {
            const code = document.createElement("code");
            t.replaceWith(code);
            code.appendChild(t);
        }
    }
    pruneEmptyInline(root);
    mergeAdjacent(root);
    const live = texts.filter((t) => t.isConnected);
    if (live.length > 0) selectTexts(live[0]!, live[live.length - 1]!);
}

/** The root-level blocks that `range` touches. */
export function topBlocks(root: HTMLElement, range: Range): HTMLElement[] {
    const top = (n: Node): HTMLElement | null => {
        let cur: Node | null = n;
        while (cur && cur.parentNode !== root) cur = cur.parentNode;
        return cur && cur.nodeType === 1 ? (cur as HTMLElement) : null;
    };
    const a = top(range.startContainer);
    const b = top(range.endContainer);
    if (!a || !b) return [];
    const out: HTMLElement[] = [];
    for (let n: Node | null = a; n; n = n.nextSibling) {
        if (n.nodeType === 1) out.push(n as HTMLElement);
        if (n === b) break;
    }
    return out;
}

function paragraphOf(text: string): HTMLElement {
    const p = document.createElement("p");
    if (text) p.textContent = text; else p.appendChild(document.createElement("br"));
    return p;
}

/** Block quote on/off for the blocks the selection touches. */
export function toggleQuote(root: HTMLElement): void {
    const range = selectionRange(root);
    if (!range) return;
    const blocks = topBlocks(root, range);
    if (blocks.length === 0) return;
    const quotes = blocks.filter((b) => b.tagName === "BLOCKQUOTE");
    if (quotes.length === blocks.length) {
        // Unwrap: the quote's blocks become ordinary blocks again.
        for (const q of quotes) unwrap(q);
        return;
    }
    const q = document.createElement("blockquote");
    blocks[0]!.before(q);
    for (const b of blocks) {
        if (b.tagName === "BLOCKQUOTE") { while (b.firstChild) q.appendChild(b.firstChild); b.remove(); }
        else q.appendChild(b);
    }
    if (range.startContainer.isConnected) {
        const r = document.createRange();
        r.selectNodeContents(q.lastElementChild ?? q);
        r.collapse(false);
        setSelection(r);
    }
}

/** Code block on/off for the blocks the selection touches: their text becomes one <pre>. */
export function toggleCodeBlock(root: HTMLElement): void {
    const range = selectionRange(root);
    if (!range) return;
    const blocks = topBlocks(root, range);
    if (blocks.length === 0) return;
    if (blocks.every((b) => b.tagName === "PRE")) {
        for (const pre of blocks) {
            const lines = (pre.textContent ?? "").replace(/\u200B/g, "").replace(/\n+$/, "").split("\n");
            const frag = document.createDocumentFragment();
            lines.forEach((l) => frag.appendChild(paragraphOf(l)));
            pre.replaceWith(frag);
        }
        return;
    }
    const lines: string[] = [];
    for (const b of blocks) {
        if (b.tagName === "UL" || b.tagName === "OL") b.querySelectorAll("li").forEach((li) => lines.push(li.textContent ?? ""));
        else lines.push(b.innerText ?? b.textContent ?? "");
    }
    const pre = document.createElement("pre");
    pre.textContent = lines.join("\n").replace(/\u00a0/g, " ").replace(/\u200B/g, "") + "\n";
    blocks[0]!.before(pre);
    blocks.forEach((b) => b.remove());
    const r = document.createRange();
    r.setStart(pre.firstChild!, Math.max(0, (pre.firstChild as Text).length - 1));
    r.collapse(true);
    setSelection(r);
}

/** A horizontal rule after the current block, and a fresh paragraph to carry on in. */
export function insertRule(root: HTMLElement): void {
    const range = selectionRange(root);
    if (!range) return;
    const blocks = topBlocks(root, range);
    const last = blocks[blocks.length - 1];
    const hr = document.createElement("hr");
    const p = paragraphOf("");
    if (last) { last.after(hr); hr.after(p); } else { root.append(hr, p); }
    const r = document.createRange();
    r.setStart(p, 0);
    r.collapse(true);
    setSelection(r);
}

/** Strip colour, highlight, size, bold, italic, underline, strike and code from the selection (links and mentions stay). */
export function clearFormatting(root: HTMLElement): void {
    const range = selectionRange(root);
    if (!range || range.collapsed) return;
    const texts = textNodesInRange(range, root);
    for (const t of texts) {
        let guard = 0;
        for (let anc = t.parentElement; anc && anc !== root && guard < 20; guard++) {
            const next: HTMLElement | null = anc.parentElement;
            if (INLINE_WRAPPERS.has(anc.tagName) && !isChip(anc)) {
                isolate(t, anc);
                unwrap(anc);
            }
            anc = next;
        }
    }
    pruneEmptyInline(root);
    root.normalize();
    const live = texts.filter((t) => t.isConnected);
    if (live.length > 0) selectTexts(live[0]!, live[live.length - 1]!);
}

export interface LinkContext {
    text: string;
    href: string;
    existing: boolean;
}

/** What the Link dialog should start with: the link under the caret, or the selected text. */
export function linkContext(root: HTMLElement): LinkContext {
    const range = selectionRange(root);
    if (!range) return { text: "", href: "", existing: false };
    const a = closestIn(range.startContainer, root, (el) => el.tagName === "A");
    if (a) return { text: a.textContent ?? "", href: a.getAttribute("href") ?? "", existing: true };
    return { text: range.toString().replace(ZWSP, ""), href: "", existing: false };
}

export function isSafeLink(href: string): boolean {
    return /^(?:https?:\/\/|mailto:)/i.test(href.trim());
}

/** Turn a typed address into a usable one: "example.com" -> "https://example.com". */
export function normalizeLinkAddress(raw: string): string {
    const v = raw.trim();
    if (!v) return "";
    if (isSafeLink(v)) return v;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return `mailto:${v}`;
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return ""; // javascript:, data:, file: ... never
    return `https://${v}`;
}

/** Insert or edit a link at `range` (the selection when the dialog opened). */
export function applyLink(root: HTMLElement, range: Range, text: string, href: string): void {
    const clean = normalizeLinkAddress(href);
    if (!clean) return;
    const label = text.trim() || clean;
    const existing = closestIn(range.startContainer, root, (el) => el.tagName === "A");
    if (existing) {
        existing.setAttribute("href", clean);
        if (existing.textContent !== label) existing.textContent = label;
        const r = document.createRange();
        r.setStartAfter(existing);
        r.collapse(true);
        setSelection(r);
        return;
    }
    const a = document.createElement("a");
    a.setAttribute("href", clean);
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
    a.textContent = label;
    range.deleteContents();
    range.insertNode(a);
    const tail = document.createTextNode(ZWSP);
    a.after(tail);
    const r = document.createRange();
    r.setStart(tail, 1);
    r.collapse(true);
    setSelection(r);
}

export function removeLink(root: HTMLElement): void {
    const range = selectionRange(root);
    if (!range) return;
    const a = closestIn(range.startContainer, root, (el) => el.tagName === "A");
    if (a) unwrap(a);
}

// ── Selection snapshots for the editor's own undo history ────────────────────

export interface SelectionSnapshot {
    start: number[];
    startOffset: number;
    end: number[];
    endOffset: number;
}

function pathOf(root: Node, node: Node): number[] {
    const path: number[] = [];
    for (let n: Node | null = node; n && n !== root; n = n.parentNode) {
        const parent: Node | null = n.parentNode;
        if (!parent) break;
        path.unshift(Array.prototype.indexOf.call(parent.childNodes, n) as number);
    }
    return path;
}

function nodeAt(root: Node, path: number[]): Node {
    let n: Node = root;
    for (const i of path) {
        const next = n.childNodes[Math.min(i, n.childNodes.length - 1)];
        if (!next) break;
        n = next;
    }
    return n;
}

export function snapshotSelection(root: HTMLElement): SelectionSnapshot | null {
    const r = selectionRange(root);
    if (!r) return null;
    return {
        start: pathOf(root, r.startContainer), startOffset: r.startOffset,
        end: pathOf(root, r.endContainer), endOffset: r.endOffset
    };
}

export function restoreSelection(root: HTMLElement, snap: SelectionSnapshot | null): void {
    const r = document.createRange();
    if (!snap) {
        r.selectNodeContents(root);
        r.collapse(false);
        setSelection(r);
        return;
    }
    const clamp = (node: Node, off: number) => Math.min(off, node.nodeType === 3 ? (node as Text).length : node.childNodes.length);
    const s = nodeAt(root, snap.start);
    const e = nodeAt(root, snap.end);
    r.setStart(s, clamp(s, snap.startOffset));
    r.setEnd(e, clamp(e, snap.endOffset));
    setSelection(r);
}
